/**
 * Sign in — one page, three audiences.
 *
 * *** WHY TABS RATHER THAN THREE PAGES ***
 * A student who lands on the admin form has no way to tell they are in the
 * wrong place: the field says "Email" and they have an email. Three visible
 * tabs make the choice obvious before anything is typed, and wrong-tab
 * recovery is one click instead of hunting for another URL.
 *
 * *** THE SLUG IS THE INSTITUTE ***
 * Students sign in with a phone number, which cannot say which institute
 * they belong to — the same number could exist in two, and this portal is
 * one deployment serving every client with no app key in the bundle. The
 * slug comes from the URL (`/login/sure60-gurukul`) and is already public:
 * it is in the merchant page address the payment gateway requires.
 *
 * Without a slug the student tab cannot work, so it is hidden rather than
 * shown broken. `/login` stays exactly what it was — the platform's admin
 * entrance.
 */

import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { rememberEntrance, useAuth } from '@/lib/auth'
import { ApiError, authApi } from '@/lib/api'
import { TextField } from '@/components/ui/Field'
import { Button } from '@/components/ui/Button'
import { AuthBrand } from './AuthBrand'
import { PolicyLinksFooter } from '@/components/ui/PolicyLinks'
import { useInstituteBrand } from './useInstituteBrand'
import styles from './LoginTabs.module.css'

type Tab = 'student' | 'mentor' | 'admin'

/** The phone the server stores carries a country code; the field does not. */
const PHONE_DIGITS = 10

/**
 * Turn what was typed into what the server stores.
 *
 * *** THE SERVER MATCHES THE NUMBER EXACTLY ***
 * `app_user.phone` holds `+919821465264`. The OTP lookup is a straight
 * equality check — it strips spaces and hyphens but adds nothing — so a bare
 * ten digits finds no account and the server answers "unknown phone". It
 * still returns 200, by design, so that the endpoint cannot be used to
 * discover who has an account. The result is a form that silently never
 * sends a code.
 *
 * The Flutter app has done this since it shipped (`normalisePhone` in
 * register_screen.dart). This is the same rule, in the one place the web
 * form needed it.
 */
function toStoredPhone(raw: string): string {
  const cleaned = raw.replace(/[^\d+]/g, '')
  if (cleaned.startsWith('+')) return cleaned
  if (cleaned.length === PHONE_DIGITS) return `+91${cleaned}`
  return `+${cleaned}`
}

export function LoginTabs() {
  const { slug } = useParams<{ slug?: string }>()
  const { login, mentorLogin, studentLogin } = useAuth()
  const { brand, loading: brandLoading } = useInstituteBrand(slug)

  /*
    *** THE ENTRANCE IS REMEMBERED ON ARRIVAL, NOT ONLY ON SIGN-OUT ***
    Logout saves the slug off the session, which covers everyone who signs
    in against this build. This covers the rest: someone who opens the
    institute link and does not finish signing in, and anyone whose session
    predates `tenant_slug`. Either way the next bounce goes to their own
    door instead of the admin one.
  */
  useEffect(() => {
    if (slug) rememberEntrance(slug)
  }, [slug])

  /*
    A student arriving at an institute URL is almost always a student, so
    that tab leads. On the bare /login it is admins, and the student tab is
    not offered at all because there is no institute to sign in to.
  */
  const [tab, setTab] = useState<Tab>(slug ? 'student' : 'admin')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const switchTab = (next: Tab) => {
    setTab(next)
    // Clearing on switch: a half-typed password left behind a tab change is
    // a password the next form might submit.
    setError(null)
    setNotice(null)
    setCodeSent(false)
    setCode('')
  }

  const describe = (caught: unknown, fallback: string): string => {
    if (caught instanceof ApiError) {
      /*
        *** ONE MESSAGE FOR EVERY CREDENTIAL FAILURE ***
        The server deliberately answers the same whether the account is
        unknown or the secret is wrong; distinguishing them would let someone
        enumerate accounts. The client must not undo that by being helpful.
      */
      return caught.status === 401 ? fallback : caught.message
    }
    return 'Could not reach the server. Check your connection.'
  }

  const handleCredentials = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      if (tab === 'mentor') {
        // The tab only exists when a slug does, so this is never undefined.
        await mentorLogin(email.trim(), password, slug ?? '')
      } else {
        await login(email.trim(), password)
      }
      // No navigation: the router swaps the tree once `isAuthenticated`
      // flips, and pushing a route here would race with that.
    } catch (caught) {
      setError(
        describe(caught, 'Those details did not match. Check your email and password.'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendCode = async (event: FormEvent) => {
    event.preventDefault()
    if (!slug) return
    setError(null)
    setSubmitting(true)
    try {
      await authApi.requestStudentOtp(slug, toStoredPhone(phone))
      setCodeSent(true)
      /*
        *** DELIBERATELY NON-COMMITTAL ***
        The server answers identically whether or not the number is
        registered, so this message must too. "We have sent a code" would
        confirm an account exists.
      */
      setNotice('If that number is registered, a code has been sent to it.')
    } catch (caught) {
      setError(describe(caught, 'Could not send a code. Check the number.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault()
    if (!slug) return
    setError(null)
    setSubmitting(true)
    try {
      await studentLogin(slug, toStoredPhone(phone), code.trim())
    } catch (caught) {
      setError(describe(caught, 'That code is not right, or it has expired.'))
    } finally {
      setSubmitting(false)
    }
  }

  const digits = phone.replace(/\D/g, '')
  const phoneReady = digits.length === PHONE_DIGITS

  /*
    *** THE BARE /login IS THE PLATFORM ADMIN ENTRANCE ***
    Admins sign in at either address: their email names their institute.
    Students and mentors need the institute named FOR them, which only the
    slug link does — a phone number cannot say which institute it belongs
    to, and the same email can be a mentor at two of them.

    Offering a Mentor tab on the bare /login was a real bug: the server has
    no email lookup for mentors, so it answered "This application build is
    not supported" and a mentor had no way in at all.
  */
  const tabs: Array<{ id: Tab; label: string }> = slug
    ? [
        { id: 'student', label: 'Student' },
        { id: 'mentor', label: 'Mentor' },
        { id: 'admin', label: 'Admin' },
      ]
    : [{ id: 'admin', label: 'Admin' }]

  /*
    A single tab is a label, not a choice. Hiding the strip on the bare
    /login stops it reading as though other options exist but are missing.
  */
  const showTabs = tabs.length > 1

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/*
          *** WHOSE ENTRANCE THIS IS ***
          At `/login/<slug>` the institute's own name and logo, because the
          page belongs to them. The bare `/login` is the platform's
          administrator door and keeps ours.

          The subtitle still says WHO it is for, so a mentor who lands on
          the wrong one learns it rather than failing to sign in.
        */}
        <AuthBrand
          brand={brand}
          loading={brandLoading}
          subtitle={slug ? 'Sign in to your institute' : 'Administrator sign-in'}
        />

        {showTabs && (
        <div className={styles.tabs} role="tablist" aria-label="Who are you signing in as">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
              onClick={() => switchTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        )}

        {tab === 'student' && slug ? (
          <form
            onSubmit={codeSent ? handleVerify : handleSendCode}
            className={styles.form}
            noValidate
          >
            <TextField
              label="Phone number"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="10-digit mobile number"
              autoComplete="tel"
              autoFocus
              required
              /* Locked once a code is out: changing the number here would
                 verify a code against a different phone. */
              disabled={submitting || codeSent}
              hint={
                codeSent ? undefined : 'The number your institute has for you.'
              }
            />

            {codeSent && (
              <>
                <TextField
                  label="Code"
                  inputMode="numeric"
                  value={code}
                  /*
                    *** FILTERED, NOT JUST maxLength ***
                    `maxLength` alone still accepts six LETTERS, and the
                    code is always six digits. Stripping non-digits on the
                    way in also makes a pasted "123 456" work rather than
                    silently failing against the server's exact match.
                  */
                  onChange={(event) =>
                    setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="6-digit code"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    setCodeSent(false)
                    setCode('')
                    setNotice(null)
                  }}
                >
                  Use a different number
                </button>
              </>
            )}

            {notice && <p className={styles.notice}>{notice}</p>}
            {error && (
              <div className={styles.error} role="alert">
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={codeSent ? code.length < 4 : !phoneReady}
            >
              {codeSent ? 'Sign in' : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleCredentials} className={styles.form} noValidate>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              disabled={submitting}
            />

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />

            {error && (
              <div className={styles.error} role="alert">
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!email || !password}
            >
              Sign in
            </Button>
          </form>
        )}

        {/*
          *** THE STUDENT LINE USED TO BE WRONG ***
          It said "your institute creates it for you", which was true only
          while the web had no sign-up. It does now, so the line is a link —
          and it keeps the slug, because a student who follows it must land
          at their own institute's form, not a generic one.

          Mentors get no such link: a mentor account is created by an
          administrator and there is no self-service path for one.
        */}
        <p className={styles.footnote}>
          {tab === 'student' && slug ? (
            <>
              No account? <Link to={`/signup/${slug}`}>Create one</Link>
            </>
          ) : !slug ? (
            'Students and mentors: use your institute’s own sign-in link.'
          ) : (
            'Trouble signing in? Contact your institute.'
          )}
        </p>

        {/*
          *** OUTSIDE THE CARD, AND REQUIRED ***
          The app shows these on both sign-in screens; the stores and the
          payment gateway were both pointed at the same pages. With a slug
          they are the INSTITUTE's policies — the student's agreement is
          with them, not with us — and without one they fall back to the
          platform's two.
        */}
        <PolicyLinksFooter slug={slug} />
      </div>
    </div>
  )
}
